import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Ugyldig lenke – mangler token');
      setTokenValid(false);
      setValidating(false);
      return;
    }

    // Get invite info
    fetch(`${API_BASE_URL}/api/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setTokenValid(true);
          setInviteInfo(data);
        } else {
          const data = await res.json().catch(() => ({}));
          setTokenValid(false);
          if (data.code === 'INVITE_EXPIRED') {
            setError('Invitasjonen har utløpt. Be om en ny invitasjon.');
          } else if (data.code === 'INVITE_NOT_PENDING') {
            setError('Denne invitasjonen er ikke lenger gyldig.');
          } else {
            setError('Ugyldig eller utløpt invitasjon.');
          }
        }
      })
      .catch(() => {
        setTokenValid(false);
        setError('Kunne ikke hente invitasjonsinformasjon. Prøv igjen senere.');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // For new accounts, validate password
    if (!inviteInfo?.accountExists) {
      if (!fullName.trim()) {
        setError('Vennligst oppgi navnet ditt.');
        return;
      }
      if (password.length < 8) {
        setError('Passordet må være minst 8 tegn.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passordene er ikke like.');
        return;
      }
    }

    setLoading(true);
    try {
      const body = { token };
      if (!inviteInfo?.accountExists) {
        body.fullName = fullName.trim();
        body.password = password;
      }

      const res = await fetch(`${API_BASE_URL}/api/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Noe gikk galt. Prøv igjen.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <header className="auth-header">
            <span className="auth-badge">KI Chat</span>
            <h1>Henter invitasjon...</h1>
            <p>Vennligst vent mens vi henter invitasjonsinformasjonen.</p>
          </header>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <header className="auth-header">
            <span className="auth-badge">KI Chat</span>
            <h1>Ugyldig invitasjon</h1>
            <p>{error || 'Denne invitasjonen er ikke gyldig lenger.'}</p>
          </header>
          <footer className="auth-footer">
            <p>Kontakt personen som inviterte deg for å få en ny invitasjon.</p>
          </footer>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <header className="auth-header">
            <span className="auth-badge auth-badge-success">🎉</span>
            <h1>Velkommen til {inviteInfo?.companyName}!</h1>
            <p>
              {inviteInfo?.accountExists
                ? 'Du er nå lagt til i selskapet.'
                : 'Kontoen din er opprettet og du er lagt til i selskapet.'}
            </p>
          </header>
          <Link to="/login" className="button button-primary">
            Gå til innlogging
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <header className="auth-header">
          <span className="auth-badge">KI Chat</span>
          <h1>Du er invitert! 🎉</h1>
          <p>
            {inviteInfo?.inviterName
              ? `${inviteInfo.inviterName} har invitert deg til `
              : 'Du er invitert til '}
            <strong>{inviteInfo?.companyName}</strong>
          </p>
        </header>

        <form className="auth-form" onSubmit={handleSubmit}>
          {inviteInfo?.accountExists ? (
            <div className="invite-info-box">
              <p>
                Du har allerede en konto med e-postadressen{' '}
                <strong>{inviteInfo.email}</strong>.
              </p>
              <p>Klikk på knappen under for å bli med i selskapet.</p>
            </div>
          ) : (
            <>
              <div className="invite-info-box">
                <p>
                  Opprett en konto for <strong>{inviteInfo?.email}</strong>
                </p>
              </div>
              <label className="auth-label" htmlFor="fullName">
                Fullt navn
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  className="input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Ola Nordmann"
                  autoComplete="name"
                  required
                />
              </label>
              <label className="auth-label" htmlFor="password">
                Velg passord
                <input
                  id="password"
                  name="password"
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minst 8 tegn"
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </label>
              <label className="auth-label" htmlFor="confirmPassword">
                Bekreft passord
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Skriv passordet på nytt"
                  autoComplete="new-password"
                  required
                />
              </label>
            </>
          )}
          {error && <div className="auth-error">⚠️ {error}</div>}
          <button type="submit" className="button button-primary" disabled={loading}>
            {loading
              ? 'Behandler...'
              : inviteInfo?.accountExists
                ? 'Bli med i selskapet'
                : 'Opprett konto og bli med'}
          </button>
        </form>

        <footer className="auth-footer">
          <p>
            Allerede registrert? <Link to="/login">Logg inn her</Link>
          </p>
        </footer>
      </div>

      <style>{`
        .invite-info-box {
          background: rgba(249, 115, 22, 0.08);
          border: 1px solid rgba(249, 115, 22, 0.2);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        }
        .invite-info-box p {
          margin: 0;
          color: var(--text-muted, #64748b);
        }
        .invite-info-box p + p {
          margin-top: 8px;
        }
      `}</style>
    </div>
  );
};

export default AcceptInvitePage;
